import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { getErrorText } from '@common/utils';
import { BehaviorSubject, defer, merge, Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, debounceTime, finalize, retry, startWith, switchMap, tap } from 'rxjs/operators';

const debounceTimeMs = 300;
const maxAttempts = 4;
const retryDelayMultiplier = 1000;

@Injectable()
export class IntervalAutoFetchService {
  #isRequestInProgress = false;
  readonly #forceRefresh$ = new Subject<void>();

  public readonly updateInterval = new FormControl<number>(30, { nonNullable: true });
  public readonly autoFetchError$ = new BehaviorSubject<string>('');

  /*
   * Manually triggers an immediate fetch
   */
  public forceFetch(): void {
    this.#forceRefresh$.next();
  }

  getAutoFetch<TResult>(request$: Observable<TResult>): Observable<TResult> {
    return this.updateInterval.valueChanges.pipe(
      startWith(this.updateInterval.value),
      debounceTime(debounceTimeMs),
      switchMap((intervalSec) => this.createFetchObservable(intervalSec, request$))
    );
  }

  private createFetchObservable<TResult>(intervalSec: number, request$: Observable<TResult>): Observable<TResult> {
    return merge(this.#forceRefresh$, timer(0, intervalSec * retryDelayMultiplier)).pipe(
      switchMap(() => {
        if (this.#isRequestInProgress) {
          return throwError(() => new Error('Request already in progress'));
        }

        this.#isRequestInProgress = true;
        return this.executeRequestWithRetry(request$);
      })
    );
  }

  private executeRequestWithRetry<TResult>(request$: Observable<TResult>): Observable<TResult> {
    return defer(() => request$).pipe(
      retry({
        count: maxAttempts,
        delay: (error, retryCount) => this.calculateRetryDelay(error, retryCount),
        resetOnSuccess: true
      }),
      tap({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err)
      }),
      catchError((err) => {
        this.handleError(err);

        return throwError(() => err);
      }),
      finalize(() => {
        this.#isRequestInProgress = false;
      })
    );
  }

  private calculateRetryDelay(error: HttpErrorResponse, retryCount: number): Observable<number> {
    const delayMs = retryCount * this.updateInterval.value * retryDelayMultiplier;

    this.handleError(error);

    return timer(delayMs);
  }

  private handleSuccess(): void {
    this.autoFetchError$.next('');
  }

  private handleError(res: HttpErrorResponse): void {
    const errorMessage = getErrorText(res);

    this.autoFetchError$.next(errorMessage);
  }
}
